import { useState } from 'react';
import { AlertCircle, Inbox, Paperclip } from 'lucide-react';
import { Pager } from '@/components/common';
import { useIssues, useUpdateIssue } from '@/services/queries/useSupportQueries';
import { StatusPill, toneFor } from '@/shared/ui/status-pill';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/lib/utils';
import type { Issue } from '@/types';
import { IssueThread } from './IssueThread';

/**
 * The lab's support queue, as the design draws it: a list beside a detail pane.
 *
 * ── Why master/detail rather than a stack of cards ──────────────────────────
 *
 * It used to be a list of expanding cards. The design is right to change it:
 * the queue is something you scan and the issue is something you read, and
 * doing both in one column means either the list is too tall to scan or the
 * issue is too cramped to read. Side by side, choosing one does not lose sight
 * of the others.
 *
 * Weighted 1:2 with 320px and 420px bases, transcribed - so the list holds its
 * width and the reading pane takes the slack.
 *
 * ── Every issue, not the caller's own ───────────────────────────────────────
 *
 * A blocked workstation is the lab owner's problem too, and an issue only its
 * reporter can see gets re-filed by the next person who hits the same thing.
 */

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  // "Waiting on you", not "waiting on user": this is read by the user.
  waiting_on_user: 'Waiting on you',
  resolved: 'Resolved',
  closed: 'Closed',
};

const CATEGORY_LABEL: Record<string, string> = {
  installation: 'Installation',
  licensing: 'Licensing',
  crash: 'Crash',
  export: 'Export',
  scan_import: 'Scan import',
  other: 'Other',
};

/** The design writes dates as "19 Aug 2026". */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The short reference, derived from the id rather than stored.
 *
 * The design writes "ISS-2871", which implies a sequence column we do not have.
 * Six characters of the real id is the thing to quote at support; a fabricated
 * ISS-nnnn would match nothing in the database.
 *
 * Must stay the same as the server's ticketRef (server/src/modules/tickets):
 * the reply and resolution emails and the ops queue quote it, so the lab sees
 * one reference everywhere. A uuid's first hyphen is at index 8, so these six
 * characters are the same with or without hyphens.
 */
function reference(issue: Issue): string {
  return `ISS-${issue.id.slice(0, 6).toUpperCase()}`;
}

/** The 3px left mark the design puts on each row, coloured by status. */
const MARK: Record<string, string> = {
  positive: 'border-l-pill-positive-foreground',
  caution: 'border-l-pill-caution-foreground',
  negative: 'border-l-pill-negative-foreground',
  info: 'border-l-pill-info-foreground',
  inert: 'border-l-transparent',
};

function QueueRow({
  issue,
  selected,
  onSelect,
}: {
  issue: Issue;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'w-full border-b border-l-[3px] border-b-border px-[18px] py-3 text-left transition-colors',
        MARK[toneFor(issue.status)],
        selected ? 'bg-muted/60' : 'hover:bg-muted/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10.5px] text-muted-foreground">{reference(issue)}</span>
        <StatusPill status={issue.status} minWidth={88}>
          {STATUS_LABEL[issue.status] ?? issue.status}
        </StatusPill>
      </div>
      <div className="mt-1 text-[12.5px] leading-[1.4] font-medium">{issue.subject}</div>
      <div className="mt-1 text-[10.5px] text-muted-foreground">{formatDate(issue.createdAt)}</div>
    </button>
  );
}

/**
 * One issue, read: the report as it was filed, then the conversation with
 * support (IssueThread - support answers from the ops portal, and each reply is
 * also emailed), then the one state change a customer makes themselves.
 */
function IssueDetail({ issue }: { issue: Issue }) {
  const update = useUpdateIssue();
  const settled = issue.status === 'resolved' || issue.status === 'closed';

  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[0_1px_3px_rgba(15,23,42,0.05)] lg:flex-[2_1_420px]">
      {/*
        The header, transcribed: a tinted band under a 2px brand-tinted rule,
        which is what separates "what this is" from "what was said about it".
      */}
      <div className="flex flex-wrap items-start justify-between gap-6 border-b-2 border-brand-200 bg-brand-50/60 px-[26px] py-[22px]">
        <div className="min-w-0">
          <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
            <span className="font-mono text-[11px] text-muted-foreground">{reference(issue)}</span>
            <StatusPill status={issue.status}>
              {STATUS_LABEL[issue.status] ?? issue.status}
            </StatusPill>
            {issue.priority !== 'medium' && <StatusPill status={issue.priority} />}
          </div>
          <h3 className="text-lg font-semibold tracking-[-0.2px]">{issue.subject}</h3>
        </div>
        <div className="pt-0.5 text-right text-[11.5px] whitespace-nowrap text-muted-foreground">
          Opened {formatDate(issue.createdAt)}
          <br />
          {CATEGORY_LABEL[issue.category] ?? issue.category}
          {issue.resolvedAt ? ` · closed ${formatDate(issue.resolvedAt)}` : ''}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 px-[22px] py-5">
        {issue.description ? (
          <div className="flex gap-3">
            <span
              className="flex size-[30px] flex-none items-center justify-center rounded-[9px] bg-brand-50 text-[11px] font-semibold text-brand-600"
              aria-hidden="true"
            >
              YOU
            </span>
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex flex-wrap items-baseline gap-2.5">
                <span className="text-[12.5px] font-semibold">Your lab</span>
                <span className="text-[10.5px] text-muted-foreground">
                  {formatDate(issue.createdAt)}
                </span>
              </div>
              <div className="rounded-[10px] border border-border bg-muted/40 px-3.5 py-3 text-[12.5px] leading-[1.6] whitespace-pre-wrap">
                {issue.description}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-[12.5px] text-muted-foreground">
            No description was given when this was raised.
          </p>
        )}

        {issue.attachmentUrl && (
          <a
            href={issue.attachmentUrl}
            rel="noreferrer noopener"
            target="_blank"
            className="inline-flex w-fit items-center gap-1.5 text-[12.5px] font-medium text-primary underline-offset-4 hover:underline"
          >
            <Paperclip size={14} />
            Log bundle
          </a>
        )}

        <IssueThread issue={issue} />
      </div>

      <div className="border-t border-border px-[22px] py-3.5">
        {settled ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11.5px] text-muted-foreground">
              Marked {STATUS_LABEL[issue.status]?.toLowerCase() ?? issue.status}. If it comes back,
              say so and it reopens.
            </p>
            <button
              type="button"
              disabled={update.isPending}
              onClick={() => {
                update.mutate({ issueId: issue.id, status: 'open' });
              }}
              className="h-9 rounded-lg border border-input px-3.5 text-[12.5px] font-semibold hover:border-primary disabled:opacity-70"
            >
              This is still happening
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11.5px] leading-relaxed text-muted-foreground">
              Support replies here and by email, quoting{' '}
              <span className="font-mono">{reference(issue)}</span>.
            </p>
            <button
              type="button"
              disabled={update.isPending}
              onClick={() => {
                update.mutate({ issueId: issue.id, status: 'resolved' });
              }}
              className="h-9 rounded-lg border border-input px-3.5 text-[12.5px] font-semibold hover:border-primary disabled:opacity-70"
            >
              I have worked around it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Issues per page of the queue. */
const PAGE_SIZE = 4;

export function IssueQueue() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const issues = useIssues({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
  const total = issues.data?.meta?.total ?? 0;

  // A page emptied under us (fewer issues than when it was opened) steps back
  // to the last page that still has rows. Set during render, not in an
  // effect, so the empty page is never painted.
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  if (issues.data && !issues.isPlaceholderData && page > lastPage) setPage(lastPage);

  if (issues.isPending) {
    return (
      <div className="flex flex-col gap-3.5 lg:flex-row" aria-busy="true">
        <Skeleton className="h-80 w-full rounded-2xl lg:flex-[1_1_320px]" />
        <Skeleton className="h-80 w-full rounded-2xl lg:flex-[2_1_420px]" />
      </div>
    );
  }

  if (issues.isError) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-[26px] py-6 text-[12.5px] text-muted-foreground">
        <AlertCircle className="size-4 shrink-0 text-destructive" />
        <span>
          Your issues could not be loaded. This is our problem, not yours - try again shortly.
        </span>
      </div>
    );
  }

  const items = issues.data?.data ?? [];

  if (items.length === 0 && page === 0) {
    /*
     * Says nothing has been raised, rather than showing an empty pair of
     * panels. An empty table reads as "we lost your issues" to somebody who is
     * already having a bad day.
     */
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card py-10 text-center shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
        <Inbox className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">You have not raised any issues</p>
        <p className="max-w-sm text-[12.5px] leading-relaxed text-muted-foreground">
          When something goes wrong, raise an issue above and attach a screenshot link - support
          picks it up from there.
        </p>
      </div>
    );
  }

  // Falls back to the newest rather than leaving the pane empty: landing on a
  // queue with nothing selected wastes the half of the screen that does the
  // reading. `items` is newest-first from the server.
  const selected = items.find((issue) => issue.id === selectedId) ?? items[0];

  return (
    <div className="flex flex-col items-stretch gap-3.5 lg:flex-row">
      <div className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card shadow-[0_1px_3px_rgba(15,23,42,0.05)] lg:flex-[1_1_320px]">
        <div className="border-b border-border px-[18px] py-3.5 text-[13.5px] font-semibold">
          Your issues
          <span className="ml-1.5 font-normal text-muted-foreground">
            ({String(total || items.length)})
          </span>
        </div>
        {/* Dimmed, not blanked, while the next page loads. */}
        <div className={cn('transition-opacity', issues.isPlaceholderData && 'opacity-60')}>
          {items.map((issue) => (
            <QueueRow
              key={issue.id}
              issue={issue}
              selected={issue.id === selected?.id}
              onSelect={() => {
                setSelectedId(issue.id);
              }}
            />
          ))}
        </div>
        <Pager
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPage={setPage}
          className="px-[18px] py-2.5"
        />
      </div>

      {selected && <IssueDetail issue={selected} />}
    </div>
  );
}
